import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CABINET_FORMULA_TOKEN_ENTRIES,
  isCabinetDimensionFormula,
  resolveCabinetDimension,
} from '@kreart/shared';
import type { CabinetDimensionValues, CabinetFormulaError } from '@kreart/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormContext } from '@/components/form/Form';
import { useFieldError } from '@/components/form/utils';
import { cn } from '@/lib/cn';

/**
 * A cut-list dimension that may be written as a formula.
 *
 * Most pieces are the cabinet's own height, width or depth give or take a board
 * thickness. Typing `@H - 36` once instead of measuring means a resize is one
 * number rather than a whole cut list, so the field carries two values:
 *
 *   `pieces.N.lengthMm`       what the piece measures — priced, stored, printed
 *   `pieces.N.lengthFormula`  where that came from, when it was not typed
 *
 * The box shows the millimetres while you are reading the table and the formula
 * the moment you focus it, so a row is legible at a glance and editable in its
 * own terms. Typing `@` offers the dimensions.
 *
 * The value is recomputed here as you type and again on the server on save (see
 * `CabinetsService.resolvePieceFormulas`), so the two can never disagree.
 */
export function PieceDimensionField({
  index,
  field,
  label,
  dims,
}: {
  index: number;
  field: 'lengthMm' | 'widthMm';
  label: string;
  /** The cabinet's Custom dimensions — what `@H` / `@W` / `@D` measure. */
  dims: CabinetDimensionValues;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The blur timer must not outlive the field: React would otherwise set state on an unmounted
  // component — and in the test runner, after jsdom is torn down ("window is not defined").
  const blurTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    },
    [],
  );

  const name = `pieces.${index}.${field}`;
  const formulaName = `pieces.${index}.${field === 'lengthMm' ? 'lengthFormula' : 'widthFormula'}`;
  const fieldId = `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;

  const mm = String(form.watch(name) ?? '');
  const formula = String(form.watch(formulaName) ?? '');
  const schemaError = useFieldError(name);

  // `null` while the box is not being edited: the table then reads as plain
  // millimetres, which is what you want when scanning a cut list.
  const [draft, setDraft] = useState<string | null>(null);
  const [formulaError, setFormulaError] = useState<CabinetFormulaError | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Which row the arrow keys are on. Reset whenever the list opens, so it always
  // starts at the top rather than wherever it was left last time.
  const [activeIndex, setActiveIndex] = useState(0);

  const editing = draft !== null;
  const shown = editing ? draft : mm;

  const suggestions = useMemo(() => {
    const byAxis = new Map<string, string[]>();
    for (const { token, axis } of CABINET_FORMULA_TOKEN_ENTRIES) {
      byAxis.set(axis, [...(byAxis.get(axis) ?? []), token]);
    }
    return (['HEIGHT', 'WIDTH', 'DEPTH'] as const).map((axis) => ({
      axis,
      tokens: byAxis.get(axis) ?? [],
      value: axis === 'HEIGHT' ? dims.heightMm : axis === 'WIDTH' ? dims.widthMm : dims.depthMm,
    }));
  }, [dims]);

  /** Push what was typed into form state, resolving a formula as we go. */
  const commit = (next: string) => {
    setDraft(next);
    // `@` on its own is someone reaching for the list, not a broken formula, so
    // the previous millimetres stay put and nothing is flagged yet.
    const opening = next.endsWith('@');
    if (opening && !suggestOpen) setActiveIndex(0);
    setSuggestOpen(opening);

    const resolved = resolveCabinetDimension(next, dims, Number(mm) || 0);
    setFormulaError(next.trim() === '' ? null : resolved.error);
    form.setValue(name, next.trim() === '' ? '' : String(resolved.valueMm), {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue(formulaName, resolved.formula ?? '', { shouldDirty: true });
  };

  const insertToken = (token: string) => {
    // Replace the trailing `@` the user typed to open the list.
    const base = draft ?? formula ?? '';
    commit(`${base.replace(/@$/, '')}@${token}`);
    setSuggestOpen(false);
    inputRef.current?.focus();
  };

  const error =
    schemaError ??
    (formulaError
      ? t(`projectItems.cabinets.piecesTable.formulaErrors.${formulaError}`)
      : undefined);

  return (
    <div className="relative">
      <Label htmlFor={fieldId}>{label}</Label>
      <Input
        id={fieldId}
        // Controlled through watch/setValue rather than `register`, because the box
        // shows the formula or the value depending on focus. The name is still
        // set so the field is addressable like every other input on the form.
        name={name}
        ref={inputRef}
        // Text, not number: a formula is not a number, and `type="number"`
        // silently refuses to display one.
        type="text"
        inputMode={editing && isCabinetDimensionFormula(shown) ? 'text' : 'numeric'}
        className={cn('h-7 text-sm', formula && !editing && 'text-foreground')}
        value={shown}
        role="combobox"
        aria-expanded={suggestOpen}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        // The formula is the useful thing to see once you are in the box.
        title={formula || undefined}
        data-formula={formula || undefined}
        // Only seeds the draft when there is not one already: picking from the
        // suggestion list re-focuses the box, and a plain assignment here would
        // throw away the token that was just inserted.
        onFocus={() => setDraft((current) => current ?? (formula || mm))}
        onBlur={() => {
          // Radix closes the list before a click on it registers, so let the
          // click land first.
          if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
          blurTimer.current = window.setTimeout(() => {
            blurTimer.current = null;
            setDraft(null);
            setSuggestOpen(false);
          }, 120);
        }}
        onChange={(event) => commit(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && suggestOpen) {
            event.preventDefault();
            setSuggestOpen(false);
            return;
          }
          if (suggestOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            // Wraps, so holding one arrow cycles rather than sticking at an end.
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((current) => (current + step + suggestions.length) % suggestions.length);
            return;
          }
          if (event.key !== 'Enter' || !suggestOpen) return;
          // Enter takes the highlighted suggestion. With no list open it is left
          // alone, and the form's own handler steps to the next field.
          event.preventDefault();
          const picked = suggestions[activeIndex] ?? suggestions[0];
          insertToken(picked?.tokens[0] ?? '');
        }}
      />
      {suggestOpen && (
        <div
          role="listbox"
          className="absolute z-[60] mt-1 w-max min-w-full rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-lg"
        >
          {suggestions.map(({ axis, tokens, value }, index) => (
            <button
              key={axis}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'block w-full whitespace-nowrap px-2 py-1 text-left text-xs hover:bg-accent hover:text-accent-foreground',
                index === activeIndex && 'bg-accent text-accent-foreground',
              )}
              // `onMouseDown` rather than `onClick`: the input's blur would
              // otherwise tear the list down before the click completed.
              onMouseDown={(event) => {
                event.preventDefault();
                insertToken(tokens[0] ?? '');
              }}
            >
              <span className="font-semibold">@{tokens.join(' / @')}</span>{' '}
              <span className="text-muted-foreground">
                {t(
                  `projectItems.cabinets.fields.${axis === 'HEIGHT' ? 'heightMm' : axis === 'WIDTH' ? 'widthMm' : 'depthMm'}`,
                )}{' '}
                · {value}
              </span>
            </button>
          ))}
        </div>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
